import * as React from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Input } from "./input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./select";
import { Button } from "./button";
import { Label, Text, Caption } from "./typography";
import { RadioGroup, RadioGroupItem } from "./radio-group";
import { API_BASE_URL } from '../../lib/api';

/**
 * AddClosedPositionDialog - Dialog for adding a closed position (realized gain/loss)
 *
 * Supports two statement types:
 * - INDIVIDUAL: Per-ticker closed position with full details
 * - ANNUAL: Annual broker statement with total realized gain
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSuccess - Callback when position is created successfully
 */
export function AddClosedPositionDialog({
  open,
  onOpenChange,
  onSuccess,
}) {
  const [formData, setFormData] = React.useState({
    statementType: "INDIVIDUAL",
    // Individual position fields
    assetName: "",
    ticker: "",
    assetType: "Stock",
    quantitySold: "",
    salePrice: "",
    purchasePrice: "",
    dateAcquired: "",
    // Annual statement fields
    totalRealizedGain: "",
    // Common fields
    saleDate: new Date().toISOString().split("T")[0],
    platform: "",
    currency: "EUR",
    notes: "",
  });

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const ASSET_TYPES = ["Stock", "ETF", "Cryptocurrency", "Precious Metal", "Other"];
  const CURRENCIES = [
    { value: "EUR", label: "EUR - Euro" },
    { value: "USD", label: "USD - US Dollar" },
    { value: "GBP", label: "GBP - British Pound" },
    { value: "SEK", label: "SEK - Swedish Krona" },
    { value: "NOK", label: "NOK - Norwegian Krone" },
    { value: "DKK", label: "DKK - Danish Krone" },
  ];

  const isAnnual = formData.statementType === "ANNUAL";

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setFormData({
        statementType: "INDIVIDUAL",
        assetName: "",
        ticker: "",
        assetType: "Stock",
        quantitySold: "",
        salePrice: "",
        purchasePrice: "",
        dateAcquired: "",
        totalRealizedGain: "",
        saleDate: new Date().toISOString().split("T")[0],
        platform: "",
        currency: "EUR",
        notes: "",
      });
      setError(null);
    }
  }, [open]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (isAnnual) {
      if (!formData.platform.trim()) {
        return "Broker/Platform is required";
      }
      if (!formData.totalRealizedGain || isNaN(parseFloat(formData.totalRealizedGain))) {
        return "Total realized gain must be a number";
      }
      if (!formData.saleDate) {
        return "Year end date is required";
      }
    } else {
      if (!formData.assetName.trim()) {
        return "Asset name is required";
      }
      if (!formData.ticker.trim()) {
        return "Ticker symbol is required";
      }
      if (!formData.quantitySold || parseFloat(formData.quantitySold) <= 0) {
        return "Quantity sold must be a positive number";
      }
      if (!formData.salePrice || parseFloat(formData.salePrice) < 0) {
        return "Sale price must be a non-negative number";
      }
      if (!formData.purchasePrice || parseFloat(formData.purchasePrice) < 0) {
        return "Purchase price must be a non-negative number";
      }
      if (!formData.saleDate) {
        return "Sale date is required";
      }
      if (!formData.dateAcquired) {
        return "Date acquired is required";
      }
      // Validate dates
      const saleDate = new Date(formData.saleDate);
      const acquiredDate = new Date(formData.dateAcquired);
      if (saleDate < acquiredDate) {
        return "Sale date must be after acquisition date";
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        statementType: formData.statementType,
        currency: formData.currency,
        saleDate: formData.saleDate,
        notes: formData.notes || "",
      };

      if (isAnnual) {
        payload.platform = formData.platform;
        payload.totalRealizedGain = parseFloat(formData.totalRealizedGain);
      } else {
        payload.assetName = formData.assetName;
        payload.ticker = formData.ticker;
        payload.assetType = formData.assetType;
        payload.quantitySold = parseFloat(formData.quantitySold);
        payload.salePrice = parseFloat(formData.salePrice);
        payload.purchasePrice = parseFloat(formData.purchasePrice);
        payload.dateAcquired = formData.dateAcquired;
        payload.platform = formData.platform || "";
      }

      const response = await axios.post(
        `${API_BASE_URL}/transactions/closed-positions`,
        payload,
        { withCredentials: true }
      );

      if (onSuccess) {
        onSuccess(response.data);
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to create closed position:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="pb-2 flex-shrink-0">
          <DialogTitle>Add Closed Position</DialogTitle>
          <DialogDescription>
            Record a realized gain or loss from a sold investment
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 pr-1">
          {/* Error Message */}
          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive p-3">
              <Text variant="error" size="sm">
                {error}
              </Text>
            </div>
          )}

          {/* Statement Type Selection */}
          <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
            <legend className="px-2 text-sm font-medium text-muted-foreground">
              Statement Type
            </legend>
            <RadioGroup
              value={formData.statementType}
              onValueChange={(value) => handleSelectChange("statementType", value)}
              className="flex flex-col space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="INDIVIDUAL" id="individual" />
                <Label htmlFor="individual" className="text-sm cursor-pointer">
                  Individual Position (per ticker)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="ANNUAL" id="annual" />
                <Label htmlFor="annual" className="text-sm cursor-pointer">
                  Annual Statement (total from broker)
                </Label>
              </div>
            </RadioGroup>
            <Caption className="block text-muted-foreground">
              {isAnnual
                ? "Use this for annual broker statements with aggregated realized gains/losses."
                : "Use this for recording individual position sales with full details."}
            </Caption>
          </fieldset>

          {/* Individual Position Fields */}
          {!isAnnual && (
            <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
              <legend className="px-2 text-sm font-medium text-muted-foreground">
                Asset Details
              </legend>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Asset Name *</Label>
                  <Input
                    name="assetName"
                    value={formData.assetName}
                    onChange={handleInputChange}
                    placeholder="e.g., Apple Inc."
                    required={!isAnnual}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Ticker *</Label>
                  <Input
                    name="ticker"
                    value={formData.ticker}
                    onChange={handleInputChange}
                    placeholder="e.g., AAPL"
                    required={!isAnnual}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Asset Type</Label>
                  <Select
                    value={formData.assetType}
                    onValueChange={(value) => handleSelectChange("assetType", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSET_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Platform/Broker</Label>
                  <Input
                    name="platform"
                    value={formData.platform}
                    onChange={handleInputChange}
                    placeholder="e.g., Interactive Brokers"
                  />
                </div>
              </div>
            </fieldset>
          )}

          {/* Annual Statement Fields */}
          {isAnnual && (
            <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
              <legend className="px-2 text-sm font-medium text-muted-foreground">
                Annual Statement Details
              </legend>

              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Broker/Platform *</Label>
                <Input
                  name="platform"
                  value={formData.platform}
                  onChange={handleInputChange}
                  placeholder="e.g., Trading 212, Interactive Brokers"
                  required={isAnnual}
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Total Realized Gain/Loss *</Label>
                <Input
                  name="totalRealizedGain"
                  type="number"
                  step="0.01"
                  value={formData.totalRealizedGain}
                  onChange={handleInputChange}
                  placeholder="e.g., 5000.00 (use negative for loss)"
                  required={isAnnual}
                />
                <Caption className="text-muted-foreground">
                  Enter a negative number for losses
                </Caption>
              </div>
            </fieldset>
          )}

          {/* Financial Details (Individual) */}
          {!isAnnual && (
            <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
              <legend className="px-2 text-sm font-medium text-muted-foreground">
                Transaction Details
              </legend>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Quantity Sold *</Label>
                  <Input
                    name="quantitySold"
                    type="number"
                    step="0.0001"
                    value={formData.quantitySold}
                    onChange={handleInputChange}
                    placeholder="e.g., 50"
                    required={!isAnnual}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Sale Price (per unit) *</Label>
                  <Input
                    name="salePrice"
                    type="number"
                    step="0.01"
                    value={formData.salePrice}
                    onChange={handleInputChange}
                    placeholder="e.g., 180.50"
                    required={!isAnnual}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Purchase Price (per unit) *</Label>
                  <Input
                    name="purchasePrice"
                    type="number"
                    step="0.01"
                    value={formData.purchasePrice}
                    onChange={handleInputChange}
                    placeholder="e.g., 150.00"
                    required={!isAnnual}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Date Acquired *</Label>
                  <Input
                    name="dateAcquired"
                    type="date"
                    value={formData.dateAcquired}
                    onChange={handleInputChange}
                    max={formData.saleDate || new Date().toISOString().split("T")[0]}
                    required={!isAnnual}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Sale Date *</Label>
                  <Input
                    name="saleDate"
                    type="date"
                    value={formData.saleDate}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split("T")[0]}
                    required
                  />
                </div>
              </div>
            </fieldset>
          )}

          {/* Common Fields */}
          <fieldset className="space-y-3 rounded-lg border border-border/50 p-4">
            <legend className="px-2 text-sm font-medium text-muted-foreground">
              {isAnnual ? "Statement Information" : "Additional Details"}
            </legend>

            <div className="grid grid-cols-2 gap-3">
              {isAnnual && (
                <div className="space-y-1.5">
                  <Label className="text-foreground text-sm">Year End Date *</Label>
                  <Input
                    name="saleDate"
                    type="date"
                    value={formData.saleDate}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split("T")[0]}
                    required
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Currency *</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => handleSelectChange("currency", value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isAnnual && (
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-foreground text-sm">Notes</Label>
                  <Input
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Optional notes about this statement..."
                  />
                </div>
              )}
            </div>

            {!isAnnual && (
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Notes</Label>
                <Input
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Optional notes about this sale..."
                />
              </div>
            )}
          </fieldset>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Position"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddClosedPositionDialog;
